import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { enTranslations } from '../translations/en';
import { esTranslations } from '../translations/es';

type Language = 'en' | 'es';

type TranslationsType = {
  [key: string]: string | TranslationsType;
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  translations: TranslationsType;
}

const translations = {
  en: enTranslations,
  es: esTranslations,
};

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
  translations: enTranslations,
});

// Helper function to get nested translations using dot notation
const getNestedTranslation = (obj: TranslationsType, path: string): string => {
  const keys = path.split('.');
  let result: any = obj;

  for (const key of keys) {
    if (result[key] === undefined) {
      return path; // Return the key if translation doesn't exist
    }
    result = result[key];
  }

  return typeof result === 'string' ? result : path;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Get initial language from localStorage or default to 'en'
  const [language, setLanguage] = useState<Language>(() => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang') as Language;
    if (langParam && (langParam === 'en' || langParam === 'es')) {
      return langParam;
    }
    
    // Fall back to localStorage
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage && (savedLanguage === 'en' || savedLanguage === 'es') 
      ? savedLanguage 
      : 'en';
  });

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
    
    // Update html lang attribute
    document.documentElement.lang = language;
    
    // Update meta tags based on language
    updateMetaTags(language);
    
    // Update URL parameter without full page reload
    const url = new URL(window.location.href);
    url.searchParams.set('lang', language);
    window.history.replaceState({}, '', url);
  }, [language]);

  // Function to update meta tags based on selected language
  const updateMetaTags = (lang: Language) => {
    if (lang === 'en') {
      document.title = "TattooNailGenius - AI-Powered Tattoo & Nail Design Generator";
      document.querySelector('meta[name="description"]')?.setAttribute('content', 
        "Create stunning tattoo and nail art designs instantly with our AI-powered design platform. Free to use, no artistic skills required.");
      
      // Update Open Graph and Twitter tags
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', 
        "TattooNailGenius - AI-Powered Tattoo & Nail Design Generator");
      document.querySelector('meta[property="og:description"]')?.setAttribute('content', 
        "Create stunning tattoo and nail art designs instantly with our AI-powered design platform. Free to use, no artistic skills required.");
      document.querySelector('meta[property="twitter:title"]')?.setAttribute('content', 
        "TattooNailGenius - AI-Powered Tattoo & Nail Design Generator");
      document.querySelector('meta[property="twitter:description"]')?.setAttribute('content', 
        "Create stunning tattoo and nail art designs instantly with our AI-powered design platform. Free to use, no artistic skills required.");
    } else if (lang === 'es') {
      document.title = "TattooNailGenius - Generador de Diseños de Tatuajes y Uñas con IA";
      document.querySelector('meta[name="description"]')?.setAttribute('content', 
        "Crea impresionantes diseños de tatuajes y arte de uñas al instante con nuestra plataforma de diseño potenciada por IA. Gratis, sin necesidad de habilidades artísticas.");
      
      // Update Open Graph and Twitter tags
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', 
        document.getElementById('es-og-title')?.getAttribute('content') || '');
      document.querySelector('meta[property="og:description"]')?.setAttribute('content', 
        document.getElementById('es-og-description')?.getAttribute('content') || '');
      document.querySelector('meta[property="twitter:title"]')?.setAttribute('content', 
        document.getElementById('es-twitter-title')?.getAttribute('content') || '');
      document.querySelector('meta[property="twitter:description"]')?.setAttribute('content', 
        document.getElementById('es-twitter-description')?.getAttribute('content') || '');
    }
    
    // Update canonical and alternate links
    const baseUrl = window.location.origin;
    const path = window.location.pathname;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', 
      `${baseUrl}${path}${lang === 'es' ? '?lang=es' : ''}`);
    
    // Update hreflang alternates
    const enLink = document.querySelector('link[hreflang="en"]');
    const esLink = document.querySelector('link[hreflang="es"]');
    
    if (enLink) {
      enLink.setAttribute('href', `${baseUrl}${path}`);
    }
    
    if (esLink) {
      esLink.setAttribute('href', `${baseUrl}${path}?lang=es`);
    }
  };

  // Translation function
  const t = (key: string): string => {
    return getNestedTranslation(translations[language], key);
  };

  const value = {
    language,
    setLanguage,
    t,
    translations: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => useContext(LanguageContext);